import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";
import { Besokstypkod } from "@/tabs/Oppenvard/types";

type State = BaseState & {
    besokstypkoder: Map<Besokstypkod["id"], Besokstypkod>;
};

export const convertBesokstypkodData: (args: {
    besokstypkod?: Besokstypkod;
}) => { value: number; label: string } | null = ({ besokstypkod }) => {
    if (!besokstypkod) {
        return null;
    }

    return {
        value: besokstypkod.id,
        label: `${besokstypkod.code} ${besokstypkod.displayName}`,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        besokstypkoder: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "besokstypkoder";
const baseUrl = "core/kodverk/besokstypkoder/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        besokstypkod: (state: State) => [...state.besokstypkoder.values()],
        besokstypkodSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                limit: 0,
                ...props, // Skriver över limit om den finns i props
                state,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertBesokstypkodData({ besokstypkod: singleResult }));
            }

            return result;
        },
        besokstypkodById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.besokstypkoder.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addBesokstypkoder: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                besokstypkoder = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                besokstypkoder: Besokstypkod[];
            }
        ) =>
            subsetSetter<State, Besokstypkod>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: besokstypkoder,
            }),
        updateBesokstypkod: (
            state: State,
            {
                besokstypkoder,
                offset,
                subsetName,
            }: {
                besokstypkoder: Besokstypkod[];
                offset: number;
                subsetName: keyof Subsets;
            } // TODO subsetName kommer skickas in i framtiden
        ) =>
            subsetUpdateSetter<State, Besokstypkod>({
                offset,
                state,
                collectionName: moduleName,
                items: besokstypkoder,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchBesokstypkoder: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
                limit = 20,
                page = 1,
            }: {
                limit: number;
                page: number;
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Besokstypkod>({
                    filterParams,
                    state,
                    config,
                    page,
                    limit,
                    collectionName: moduleName,
                    apiUrl: baseUrl,
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addBesokstypkoder", {
                    subsetName,
                    offset,
                    besokstypkoder: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertBesokstypkodData({ besokstypkod: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        createBesokstypkod: async (
            { commit, state }: BaseActionsProps<State>,
            {
                besokstypkod,
            }: {
                besokstypkod: Besokstypkod;
            }
        ) => {
            try {
                const response = await klinikenApi.post(baseUrl, besokstypkod, getConfig({}));

                // TODO: Hur får jag den att läggas till i alla subsets som den ska ingå i?
                commit("addBesokstypkoder", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    besokstypkoder: [response.data],
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        updateBesokstypkod: async (
            { commit, state }: BaseActionsProps<State>,
            {
                besokstypkod,
            }: {
                besokstypkod: Besokstypkod;
            }
        ) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${besokstypkod.id}/`, besokstypkod, getConfig({}));

                commit("updateBesokstypkod", {
                    subsetName: "newlyUpdated",
                    besokstypkoder: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
